import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC, ReactElement } from "react"
import React from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { CompanyRegistration } from "@/graphql/codegen/graphql"
import CompanyFileForm from "@/screens/Companies/edit/components/info/components/form/CompanyFileForm"
import useCompanyFileDialogActions from "@/screens/Companies/edit/components/info/hooks/useCompanyFileDialogActions"
import type { CompanyFileFormValues } from "@/screens/Companies/edit/components/info/schema/CompanyFileSchema"
import { CompanyFileSchema } from "@/screens/Companies/edit/components/info/schema/CompanyFileSchema"

interface CompanyFileDialogProps {
  companyId: string
  onClose: () => void
  onSuccess?: () => void
  data?: CompanyRegistration
}

const CompanyFileDialog: FC<CompanyFileDialogProps> = ({ companyId, onClose, onSuccess }): ReactElement => {
  const methods = useForm<CompanyFileFormValues>({
    defaultValues: {
      companyId,
      fileType: null as any,
      file: null as any,
    },
    resolver: zodResolver(CompanyFileSchema),
    mode: "onChange",
  })

  const { setIsCancelConfirmationOpen, isCancelConfirmationOpen, canSubmit, handleSubmit, handleCancel } =
    useCompanyFileDialogActions({
      methods,
      onSuccess,
      onClose,
    })

  return (
    <>
      {isCancelConfirmationOpen && (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
      )}
      <Dialog
        open
        onClose={handleCancel}
        maxWidth='sm'
        fullWidth
        data-testid='file-upload-dialog'
        sx={{ display: isCancelConfirmationOpen ? "none" : "block" }}
      >
        <Box>
          <DialogHeader title='Upload File' onClose={handleCancel} />
          <DialogContent classes={{ root: "p-10" }}>
            <FormProvider {...methods}>
              <CompanyFileForm onSubmit={handleSubmit} />
            </FormProvider>
          </DialogContent>
          <DialogActions className='justify-between px-10 pb-10'>
            <Button appearance='outlined' onClick={handleCancel} data-testid='cancel-button'>
              Cancel
            </Button>
            <Button
              appearance='contained'
              type='submit'
              disabled={!canSubmit}
              onClick={handleSubmit}
              data-testid='submit-button'
            >
              Save
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  )
}

export default CompanyFileDialog
